var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { BigNumber } from "@ethersproject/bignumber";
import { ethers } from "ethers";
import { assetToFiat } from "./math";
import { parseUnits } from "ethers/lib/utils";
var formatUnits = ethers.utils.formatUnits;
export var calculateInitialveRBNAmount = function (rbnAmount, duration) {
    var totalMinutes = Math.round(duration.asMinutes());
    var minutesInTwoYears = 2 * 365 * 24 * 60;
    var veRbnAmount = rbnAmount
        .mul(BigNumber.from(totalMinutes))
        .div(BigNumber.from(minutesInTwoYears));
    return veRbnAmount.isNegative() ? BigNumber.from(0) : veRbnAmount;
};
export var calculateEarlyUnlockPenaltyPercentage = function (remainingDuration) {
    var minutesRemaining = Math.round(remainingDuration.asMinutes());
    var minutesInTwoyears = 2 * 365 * 24 * 60;
    return Math.min(0.75, minutesRemaining / minutesInTwoyears);
};
export var calculateAdjustedEarlyUnlockPenaltyPercentage = function (lockedAmount, penalty) {
    return (parseFloat(lockedAmount
        .sub(lockedAmount.sub(penalty))
        .mul(10000)
        .div(lockedAmount)
        .toString()) / 100);
};
export var calculateEarlyUnlockPenalty = function (lockedAmount, penaltyRebate, remainingDuration) {
    var penaltyPercent = calculateEarlyUnlockPenaltyPercentage(remainingDuration);
    // get the min between original penalty and rbnRebateAmount
    var penaltyDeduction = penaltyRebate.gt(lockedAmount.mul(Math.round((penaltyPercent * 10000) / 2)).div(10000))
        ? lockedAmount.mul(Math.round((penaltyPercent * 10000) / 2)).div(10000)
        : penaltyRebate;
    // Multiply and divide by 10,000 to preserve the accuracy, because BigNumber will remove
    // all decimals.
    // Eg. If penaltyPercent is 0.34, it will be 0 when converted to BigNumber
    // then lockedAmount.mul(0) will be 0.
    // If user locked before 1677261600, the penalty is reduced by penalty rebate amount
    return lockedAmount
        .mul(Math.round(penaltyPercent * 10000))
        .div(10000)
        .sub(penaltyDeduction);
};
/**
 * Given pool size and rewards, calculate the base APY percentage
 * @param poolSize pool size (pool size should be larger than pool reward)
 * @param poolReward pool reward
 * @param pricePerShare price per share of the vault
 * @param decimals decimals of the vault
 * @param assetPrice price of the vault asset
 * @param rbnPrice Ribbon price
 * @param lendAPY APY from lending
 * @returns APY percentage, eg. 40 (40%)
 */
export var calculateBaseRewards = function (_a) {
    var poolSize = _a.poolSize, poolReward = _a.poolReward, pricePerShare = _a.pricePerShare, decimals = _a.decimals, assetPrice = _a.assetPrice, rbnPrice = _a.rbnPrice, lendAPY = _a.lendAPY;
    var poolRewardInUSD = parseFloat(assetToFiat(poolReward, rbnPrice));
    var poolSizeInAsset = poolSize
        .mul(pricePerShare)
        .div(parseUnits("1", decimals));
    var poolSizeInUSD = parseFloat(assetToFiat(poolSizeInAsset, assetPrice, decimals));
    if (poolSizeInUSD <= 0) {
        return 0;
    }
    var baseReward = (poolRewardInUSD / poolSizeInUSD) * 52 * 100;
    return lendAPY ? baseReward + lendAPY : baseReward;
};
// Calculates the boost for staking in vault gauges
export var calculateBoostMultiplier = function (_a) {
    var workingBalance = _a.workingBalance, workingSupply = _a.workingSupply, gaugeBalance = _a.gaugeBalance, poolLiquidity = _a.poolLiquidity, veRBNAmount = _a.veRBNAmount, totalVeRBN = _a.totalVeRBN;
    var l = Number(gaugeBalance.toString());
    var L = Number(poolLiquidity.toString()) + l;
    var veRBNAmt = parseFloat(formatUnits(veRBNAmount, 18));
    var totalVeRBNAmt = parseFloat(formatUnits(totalVeRBN, 18));
    var workingBalanceAmt = Number(workingBalance.toString());
    var workingSupplyAmt = Number(workingSupply.toString());
    var TOKENLESS_PRODUCTION = 40;
    var lim = (l * TOKENLESS_PRODUCTION) / 100;
    lim +=
        (((L * veRBNAmt) / totalVeRBNAmt) * (100 - TOKENLESS_PRODUCTION)) / 100;
    lim = Math.min(l, lim);
    var old_bal = workingBalanceAmt;
    var noboost_lim = (TOKENLESS_PRODUCTION * l) / 100;
    var noboost_supply = workingSupplyAmt + noboost_lim - old_bal;
    var _working_supply = workingSupplyAmt + lim - old_bal;
    return lim / _working_supply / (noboost_lim / noboost_supply);
};
/**
 * Given the base rewards and multiplier, calculates the boosted rewards percentage
 * @param baseRewardsPercentage APY in percentage. Commonly the result from calculateBaseRewards(). eg. 10 (10%)
 * @param boostedMultiplier Multiplier. Commonly the result from calculateBoostMultiplier(). eg. 1.5 (1.5x)
 * @returns The boosted rewards percentage. eg. 5 (5%)
 */
export var calculateBoostedRewards = function (baseRewardsPercentage, boostedMultiplier) {
    return boostedMultiplier > 0
        ? baseRewardsPercentage * boostedMultiplier - baseRewardsPercentage
        : 0;
};
export var calculateClaimableRbn = function (_a) {
    var currentDate = _a.currentDate, periodTimestamp = _a.periodTimestamp, integrateInvSupply = _a.integrateInvSupply, integrateFraction = _a.integrateFraction, integrateInvSupplyOf = _a.integrateInvSupplyOf, futureEpochTime = _a.futureEpochTime, inflation_rate = _a.inflation_rate, minterRate = _a.minterRate, isKilled = _a.isKilled, workingSupply = _a.workingSupply, workingBalance = _a.workingBalance, mintedRBN = _a.mintedRBN, gaugeContractAddress = _a.gaugeContractAddress, gaugeControllerContract = _a.gaugeControllerContract;
    return __awaiter(void 0, void 0, void 0, function () {
        var WEEK, currentTime, trimDecimals, _integrate_inv_supply, rate, new_rate, prev_future_epoch, prev_week_time, week_time, i, dt, w;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    WEEK = 604800;
                    currentTime = Math.round(currentDate.getTime() / 1000);
                    trimDecimals = function (number) {
                        return Number(number.toFixed(0));
                    };
                    _integrate_inv_supply = integrateInvSupply;
                    rate = inflation_rate;
                    new_rate = rate;
                    prev_future_epoch = futureEpochTime;
                    if (currentTime >= prev_future_epoch) {
                        new_rate = minterRate;
                    }
                    if (isKilled) {
                        // Stop distributing inflation as soon as killed
                        rate = BigNumber.from(0);
                        new_rate = BigNumber.from(0);
                    }
                    if (!(currentTime > periodTimestamp)) return [3 /*break*/, 4];
                    prev_week_time = periodTimestamp;
                    week_time = Math.min(trimDecimals((periodTimestamp + WEEK) / WEEK) * WEEK, currentTime);
                    i = 0;
                    _b.label = 1;
                case 1:
                    if (!(i < 500)) return [3 /*break*/, 4];
                    dt = week_time - prev_week_time;
                    return [4 /*yield*/, gaugeControllerContract["gauge_relative_weight(address,uint256)"](gaugeContractAddress, trimDecimals(prev_week_time / WEEK) * WEEK)];
                case 2:
                    w = _b.sent();
                    if (workingSupply.gt(0)) {
                        if (prev_future_epoch >= prev_week_time &&
                            prev_future_epoch < week_time &&
                            rate !== new_rate) {
                            _integrate_inv_supply = _integrate_inv_supply.add(rate
                                .mul(w)
                                .mul(prev_future_epoch - prev_week_time)
                                .div(workingSupply));
                            rate = new_rate;
                            _integrate_inv_supply = _integrate_inv_supply.add(rate
                                .mul(w)
                                .mul(week_time - prev_future_epoch)
                                .div(workingSupply));
                        }
                        else {
                            _integrate_inv_supply = _integrate_inv_supply.add(new_rate.mul(w).mul(dt).div(workingSupply));
                            // On precisions of the calculation
                            // rate ~= 10e18
                            // last_weight > 0.01 * 1e18 = 1e16(if pool weight is 1 %)
                            // _working_supply ~= TVL * 1e18 ~= 1e26($100M for example)
                            // The largest loss is at dt = 1
                            // Loss is 1e-9 - acceptable
                        }
                    }
                    if (week_time === currentTime) {
                        return [3 /*break*/, 4];
                    }
                    prev_week_time = week_time;
                    week_time = Math.min(week_time + WEEK, currentTime);
                    _b.label = 3;
                case 3:
                    i++;
                    return [3 /*break*/, 1];
                case 4:
                    integrateFraction = integrateFraction.add(workingBalance
                        .mul(_integrate_inv_supply.sub(integrateInvSupplyOf))
                        .div(parseUnits("1", 18)));
                    return [2 /*return*/, integrateFraction.sub(mintedRBN)];
            }
        });
    });
};
//# sourceMappingURL=governanceMath.js.map